import { useEffect, useState } from 'react'

import { API } from '@/api'
import { Button, Divider, Loading, message, Modal, Trans } from '@/components'
import { Instance } from '@/models/instance.ts'
import { formatMillicentPrice } from '@/utils/formatter.ts'
import { cn } from '@/utils/style.ts'

import { SliderInput } from './SliderInput'

type Attachment = {
  resIDs: string[] | null
  priceMillicentPerUnit: number
  coresPerUnit?: number
  MBPerUnit?: number
}

export function UpgradeHardwareModal({
  open,
  onClose,
  instance,
}: {
  open: boolean
  instance: Instance
  onUpdate?: () => void
  onClose: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [requestedIncrements, setRequestedIncrements] = useState({ vCPU: 0, ram: 0 })
  const [attachments, setAttachments] = useState({
    ramGBPerUnit: 1,
    availableCpu: undefined as Attachment | undefined,
    availableRam: undefined as Attachment | undefined,
    requestedCpu: undefined as Attachment | undefined,
    requestedRam: undefined as Attachment | undefined,
  })
  const [costs, setCosts] = useState({
    base: 0,
    vCPU: 0,
    ram: 0,
    total: 0,
  })

  const initialVCPUCount = instance.hardware.machine.cpuIds.length
  const initialRamGB = Math.round(instance.hardware.machine.ramMB / 1024)

  const fetchAttachments = async () => {
    if (open) {
      setLoading(true)
      try {
        const res = await API.getAttachments({ inst_res_id: instance.resID })
        if (res.code === 0) {
          const { available, requested } = res
          const priceMillicent =
            instance.hardware.discountPriceMillicent || instance.hardware.priceMillicent
          const ramGBPerUnit =
            Math.round(
              (available.ramAttachments.MBPerUnit || requested.ramAttachments.MBPerUnit || 1) /
                1024,
            ) || 1

          setAttachments({
            ramGBPerUnit,
            availableCpu: available.cpuAttachments,
            availableRam: available.ramAttachments,
            requestedCpu: requested.cpuAttachments,
            requestedRam: requested.ramAttachments,
          })
          setRequestedIncrements({ vCPU: 0, ram: 0 })

          setCosts({
            base: priceMillicent,
            vCPU: 0,
            ram: 0,
            total: priceMillicent,
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchAttachments()
  }, [open, instance.resID])

  const calculateCostAdjustment = (increment: number, unit: number, pricePerUnit: number) =>
    (increment / unit) * pricePerUnit

  const handleVCPUChange = (value: number) => {
    const increment = value - initialVCPUCount
    setRequestedIncrements((prev) => ({ ...prev, vCPU: increment }))
    const newCost = calculateCostAdjustment(
      increment,
      attachments.availableCpu?.coresPerUnit || 1,
      attachments.availableCpu?.priceMillicentPerUnit || 0,
    )
    setCosts((prev) => ({ ...prev, vCPU: newCost, total: prev.base + newCost + prev.ram }))
  }

  const handleRamChange = (value: number) => {
    const increment = value - initialRamGB
    setRequestedIncrements((prev) => ({ ...prev, ram: increment }))

    const pricePerUnit =
      increment > 0
        ? attachments.availableRam?.priceMillicentPerUnit
        : attachments.requestedRam?.priceMillicentPerUnit
    const newCost = calculateCostAdjustment(increment, attachments.ramGBPerUnit, pricePerUnit || 0)
    setCosts((prev) => ({ ...prev, ram: newCost, total: prev.base + prev.vCPU + newCost }))
  }

  const onOK = () => {
    let cpuResIDs: string[] = [...(attachments.requestedCpu?.resIDs || [])]
    if (requestedIncrements.vCPU > 0) {
      const requiredCpuUnits =
        requestedIncrements.vCPU / (attachments.availableCpu?.coresPerUnit || 1)
      const additionalCpuResIDs = attachments.availableCpu?.resIDs?.slice(0, requiredCpuUnits) || []
      cpuResIDs = cpuResIDs.concat(additionalCpuResIDs)
    } else if (requestedIncrements.vCPU < 0) {
      const decrement = Math.abs(requestedIncrements.vCPU)
      cpuResIDs = cpuResIDs.slice(0, cpuResIDs.length - decrement)
    }

    let ramResIDs: string[] = [...(attachments.requestedRam?.resIDs || [])]
    if (requestedIncrements.ram > 0) {
      const requiredRamUnits = requestedIncrements.ram / attachments.ramGBPerUnit
      const additionalRamResIDs = attachments.availableRam?.resIDs?.slice(0, requiredRamUnits) || []
      ramResIDs = ramResIDs.concat(additionalRamResIDs)
    } else if (requestedIncrements.ram < 0) {
      const decrementRamUnits = Math.abs(requestedIncrements.ram) / attachments.ramGBPerUnit
      ramResIDs = ramResIDs.slice(0, ramResIDs.length - decrementRamUnits)
    }

    API.updateAttachments({
      inst_res_id: instance.resID,
      res_ids: ramResIDs.concat(cpuResIDs),
    })
      .then((res) => {
        if (res.code === 0) {
          message.success('Task submitted')
          onClose()
        } else {
          message.error('Failed to update')
        }
      })
      .finally(() => setLoading(false))
  }

  return (
    <Modal className="w-[640px]" open={open} onClose={onClose}>
      <Modal.Header>
        <Trans i18nKey="instance.list.hardware.configUpgradeModalTitle">Upgrade Hardware</Trans>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            <div className="bg-panel-gray-100 rounded-lg p-4 flex-row mb-4">
              <div className="flex justify-between t-s-m items-baseline">
                <div className="text-secondary">Cost Estimation</div>
                <div className="flex gap-x-0.5 text-panel-primary items-baseline">
                  <div className="h6">{`${formatMillicentPrice(costs.total)}/h`}</div>
                </div>
              </div>
              <Divider className="my-2" />
              <div className="flex justify-between t-sm items-baseline mb-2">
                <div className="text-secondary">Base Cost</div>
                <div className="flex items-baseline">
                  <div>{`${formatMillicentPrice(costs.base)}/h`}</div>
                </div>
              </div>
              <div className="flex justify-between t-sm items-baseline mb-2">
                <div className="text-secondary">vCPU Cost Adjustment</div>
                <div
                  className={cn('flex items-baseline', {
                    'text-red-600': costs.vCPU < 0,
                    'text-green-600': costs.vCPU > 0,
                  })}
                >
                  {`${formatMillicentPrice(costs.vCPU)}/h`}
                </div>
              </div>
              <div className="flex justify-between t-sm items-baseline">
                <div className="text-secondary">RAM Cost Adjustment</div>
                <div className="flex items-baseline">
                  <div
                    className={cn('flex items-baseline', {
                      'text-red-600': costs.ram < 0,
                      'text-green-600': costs.ram > 0,
                    })}
                  >
                    {`${formatMillicentPrice(costs.ram)}/h`}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-primary mb-2 flex">vCPU</h3>
              <SliderInput
                defaultValue={initialVCPUCount}
                min={initialVCPUCount}
                max={
                  initialVCPUCount +
                  (attachments.availableCpu?.resIDs?.length || 0) *
                    (attachments.availableCpu?.coresPerUnit || 1)
                }
                step={attachments.availableCpu?.coresPerUnit || 1}
                onChange={handleVCPUChange}
              />
            </div>
            <div className="mt-4">
              <h3 className="text-primary mb-2 flex">RAM</h3>
              <SliderInput
                defaultValue={initialRamGB}
                min={initialRamGB}
                max={
                  initialRamGB +
                  (attachments.availableRam?.resIDs?.length || 0) * attachments.ramGBPerUnit
                }
                step={attachments.ramGBPerUnit}
                onChange={handleRamChange}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" disabled={loading} onClick={onOK}>
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
